import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Recipe } from '../../models/Recipe';
import { getRecipeThumbnail } from '../../utils/RecipeService';

// A match lands right after a swipe, and the tap that produced it can carry over onto whatever
// appears underneath. Hold the buttons for a moment so nobody dismisses the match without seeing it.
const LOCK_SECONDS = 5;

type Props = {
  recipe: Recipe;
  currentUsername: string;
  partnerName: string;
  onKeepSwiping: () => void;
  onNewSession: () => void;
};

const fallCW = keyframes({
  '0%':   { transform: 'translateY(-20px) rotate(0deg)'   },
  '100%': { transform: 'translateY(110vh) rotate(540deg)' },
});

const fallCCW = keyframes({
  '0%':   { transform: 'translateY(-20px) rotate(0deg)'    },
  '100%': { transform: 'translateY(110vh) rotate(-400deg)' },
});

type Piece = {
  left: number;
  size: number;
  bg: string;
  shape: 'square' | 'circle' | 'rect';
  duration: number;
  delay: number;
  cw: boolean;
};

// Negative delays place each piece at a different point in its fall cycle at render time,
// spreading them from top to bottom immediately rather than all entering from the top at once.
const PIECES: Piece[] = [
  { left:  6, size: 10, bg: '#D97706', shape: 'square', duration: 3.2, delay:  0.0,  cw: true  },
  { left: 14, size:  7, bg: '#2b2b2b', shape: 'circle', duration: 2.8, delay: -0.2,  cw: false },
  { left: 23, size:  8, bg: '#f5c842', shape: 'rect',   duration: 3.6, delay: -0.5,  cw: true  },
  { left: 34, size: 11, bg: '#D97706', shape: 'square', duration: 2.5, delay: -0.5,  cw: false },
  { left: 43, size:  6, bg: '#c45d00', shape: 'circle', duration: 3.1, delay: -0.9,  cw: true  },
  { left: 52, size:  9, bg: '#2b2b2b', shape: 'square', duration: 2.9, delay: -1.0,  cw: false },
  { left: 60, size:  7, bg: '#D97706', shape: 'rect',   duration: 3.4, delay: -1.5,  cw: true  },
  { left: 69, size: 10, bg: '#e8b27a', shape: 'circle', duration: 2.7, delay: -1.4,  cw: false },
  { left: 77, size:  8, bg: '#D97706', shape: 'square', duration: 3.0, delay: -1.7,  cw: true  },
  { left: 86, size:  6, bg: '#2b2b2b', shape: 'rect',   duration: 2.6, delay: -1.7,  cw: false },
  { left: 11, size:  9, bg: '#e8b27a', shape: 'square', duration: 3.3, delay: -2.4,  cw: true  },
  { left: 30, size:  7, bg: '#f5c842', shape: 'circle', duration: 2.4, delay: -1.9,  cw: false },
  { left: 57, size: 11, bg: '#2b2b2b', shape: 'rect',   duration: 3.8, delay: -3.3,  cw: true  },
  { left: 74, size:  8, bg: '#e8b27a', shape: 'square', duration: 2.9, delay: -2.7,  cw: false },
];

const RecipeMatchCelebrate = ({ recipe, currentUsername, partnerName, onKeepSwiping, onNewSession }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [thumbnail, setThumbnail] = useState<string>();
  const [secondsLeft, setSecondsLeft] = useState(LOCK_SECONDS);
  const locked = secondsLeft > 0;

  // Restarts for every match, so a second one arriving while this screen is up gets its own hold.
  useEffect(() => {
    setSecondsLeft(LOCK_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [recipe.id]);

  useEffect(() => {
    if (recipe.id != null) {
      getRecipeThumbnail(recipe.id).then(blob => {
        if (blob) setThumbnail(URL.createObjectURL(blob));
      });
    }
  }, [recipe.id]);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', flex: 1,
      bgcolor: '#fff7ee', position: 'relative', overflow: 'hidden',
    }}>
      {PIECES.map((p, i) => (
        <Box key={i} sx={{
          position: 'absolute',
          top: 0,
          left: `${p.left}%`,
          width: p.size,
          height: p.shape === 'rect' ? p.size * 2 : p.size,
          bgcolor: p.bg,
          borderRadius: p.shape === 'circle' ? '50%' : 1,
          pointerEvents: 'none',
          animation: `${p.cw ? fallCW : fallCCW} ${p.duration}s ${p.delay}s infinite linear`,
        }} />
      ))}

      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 2.5, textAlign: 'center', px: 3,
      }}>
        <Typography variant="h3" fontWeight={800} color="primary" sx={{ lineHeight: 1.05, whiteSpace: 'pre-line' }}>
          {t('match_itsAMatch')}
        </Typography>

        {/* Avatar row: user — recipe photo — partner */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{
            width: 50, height: 50, borderRadius: '50%',
            border: '2px solid', borderColor: 'text.primary',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 600, color: 'text.secondary', zIndex: 1,
          }}>
            {currentUsername.charAt(0).toUpperCase()}
          </Box>

          <Box sx={{
            width: 74, height: 74, borderRadius: 3,
            border: '2.5px solid', borderColor: 'text.primary',
            mx: -1.25, zIndex: 2,
            backgroundImage: thumbnail ? `url(${thumbnail})` : undefined,
            backgroundSize: 'cover', backgroundPosition: 'center',
            bgcolor: thumbnail ? undefined : 'grey.200',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 4,
          }}>
            {!thumbnail && (
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.58rem' }}>
                photo
              </Typography>
            )}
          </Box>

          <Box sx={{
            width: 50, height: 50, borderRadius: '50%',
            border: '2px solid', borderColor: 'text.primary',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 600, color: 'text.secondary', zIndex: 1,
          }}>
            {partnerName.charAt(0).toUpperCase()}
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ color: '#8a6b4a' }}>
            {t('match_bothWant', { name: partnerName })}
          </Typography>
          <Typography variant="h6" fontWeight={700} mt={0.5}>
            {recipe.name}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 3, pb: 4 }}>
        <Button
          variant="contained"
          size="large"
          disabled={locked}
          onClick={() => navigate('/recipe/' + recipe.id)}
          sx={{ borderRadius: 8, py: 1.4, fontSize: '1rem', fontWeight: 700 }}
        >
          {t('match_viewRecipe')}
        </Button>
        <Button
          variant="outlined"
          size="large"
          disabled={locked}
          onClick={onKeepSwiping}
          sx={{ borderRadius: 8, py: 1.3, fontSize: '1rem', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
        >
          {t('match_keepSwiping')}
        </Button>
        <Button
          variant="text"
          size="small"
          disabled={locked}
          onClick={onNewSession}
          sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
        >
          {t('match_startOver')}
        </Button>
        {locked && (
          <Typography variant="caption" align="center" sx={{ color: '#8a6b4a', mt: -0.5 }}>
            {t('match_unlockIn', { seconds: secondsLeft })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RecipeMatchCelebrate;
